import { useState, useRef, useEffect, useMemo } from "react";
import useFetchContent, {
  ContentFilters,
} from "../../../hooks/useFetchContent";
import useStore from "../../../store/useStore";
import {
  mapApiResponseToContentItem,
  ContentItem,
} from "../../../utils/contentMappers";
import { getContentTypeDisplayName } from "../utils/contentTypeMapping";

// Constants
const ITEMS_PER_PAGE = 15;
const MIN_PAGE_SIZE = 50;

// Helper functions
function findRelatedContentByGenre(
  allItems: ContentItem[],
  genres: string[],
  count: number,
  excludeTypeId?: number,
  usedIds?: Set<number>
): ContentItem[] {
  return allItems
    .filter(
      (item) =>
        (!usedIds || !usedIds.has(item.id)) &&
        (!excludeTypeId || item.contentTypeId !== excludeTypeId) &&
        item.genres?.some((genre: string) => genres.includes(genre))
    )
    .slice(0, count);
}

function findTrendingContent(
  allItems: ContentItem[],
  count: number,
  usedIds?: Set<number>
): ContentItem[] {
  return allItems
    .filter((item) => !usedIds || !usedIds.has(item.id))
    .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
    .slice(0, count);
}

function addItemsToSection(
  items: ContentItem[],
  section: ContentItem[],
  usedIds?: Set<number>
): ContentItem[] {
  if (items.length === 0) return section;

  const updatedSection = [...section, ...items];

  if (usedIds) {
    items.forEach((item) => usedIds.add(item.id));
  }

  return updatedSection;
}

// Calculate items per type based on selected filters
function getItemsPerContentType(
  sectionType: "banner" | "latest" | "topPicks",
  selectedTypesCount: number
): number {
  if (sectionType === "topPicks") {
    return 3; // Top Picks section always shows top 3 tertiary picks from each non-selected content type
  }

  // Banner section logic based on content type filtering requirements:
  if (sectionType === "banner") {
    if (selectedTypesCount === 0)
      return 1; // Default: 1 latest news from each content type
    else if (selectedTypesCount === 1)
      return 3; // 1 content type selected: Show top 3 latest news
    else if (selectedTypesCount === 2)
      return 2; // 2 content types selected: Show 2 latest news per type
    else return 1; // 3+ content types selected: Show 1 latest news per type
  }

  // Latest section logic based on content type filtering requirements:
  if (selectedTypesCount === 0)
    return 3; // Default: Displays secondary latest content from all content types
  else if (selectedTypesCount === 1)
    return 3; // 1 content type selected: Show top 3 secondary latest news
  else if (selectedTypesCount === 2)
    return 2; // 2 content types selected: Show 2 secondary latest news per type
  else return 1; // 3+ content types selected: Show 1 secondary latest news per type
}

const useContentData = (debounceMs: number = 500) => {
  // For debouncing API calls
  const [debouncedApiCall, setDebouncedApiCall] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize refs to track render and API call state
  const isInitialRender = useRef(true);
  const initialApiCallMade = useRef(false);

  // Get TatvaPedia filters from the store
  const { selectedTatvaPediaFilters, tatvaPediaFilters } = useStore();

  // Extract content type data for easier access
  const contentTypeData = useMemo(() => {
    const selectedIds =
      selectedTatvaPediaFilters["Content Type"]?.map((type) =>
        Number(type.id)
      ) || [];

    const allTypes = tatvaPediaFilters["Content Type"] || [];
    const allIds = allTypes.map((type) => type.id);

    // Map contentTypeId to display name for UI
    const idToName: Record<number, string> = {};
    allTypes.forEach((type) => {
      idToName[type.id] = getContentTypeDisplayName(type.code);
    });

    return {
      selectedContentTypeIds: selectedIds,
      allContentTypes: allTypes,
      allContentTypeIds: allIds,
      contentTypeIdToName: idToName,
    };
  }, [selectedTatvaPediaFilters, tatvaPediaFilters]);

  // Debounce filter changes to prevent multiple API calls when filters change rapidly
  useEffect(() => {
    // Skip the debounce on initial render to prevent double API calls
    if (isInitialRender.current) {
      isInitialRender.current = false;
      setDebouncedApiCall(true);
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set debouncedApiCall to false to prevent API call
    setDebouncedApiCall(false);

    // Set a timeout to enable API call after debounce period
    timeoutRef.current = setTimeout(() => {
      setDebouncedApiCall(true);
    }, debounceMs);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [selectedTatvaPediaFilters, debounceMs]);

  // Prepare filters for API requests
  const prepareFilters = useMemo((): ContentFilters => {
    const filters: ContentFilters = {
      contentCountryId: [1],
      status: 489,
      sort: "publishedDate desc",
    };

    // Check if there are selected Genre filters
    if (selectedTatvaPediaFilters["Genre"]?.length > 0) {
      filters.contentGenreId = selectedTatvaPediaFilters["Genre"].map((genre) =>
        Number(genre.id)
      );
    }
    // If no genres are selected, use all available genres from tatvaPediaFilters
    else if (tatvaPediaFilters["Genre"]?.length > 0) {
      filters.contentGenreId = tatvaPediaFilters["Genre"].map(
        (genre) => genre.id
      );
    }

    // Check if there are selected Continuum filters
    if (selectedTatvaPediaFilters["Continuum"]?.length > 0) {
      filters.contentContinuumId = selectedTatvaPediaFilters["Continuum"].map(
        (cont) => Number(cont.id)
      );
    }
    // If no continuums are selected, use all available continuums from tatvaPediaFilters
    else if (tatvaPediaFilters["Continuum"]?.length > 0) {
      filters.contentContinuumId = tatvaPediaFilters["Continuum"].map(
        (cont) => cont.id
      );
    }

    // Check if there are selected Speciality filters
    if (selectedTatvaPediaFilters["Speciality"]?.length > 0) {
      filters.specialityId = selectedTatvaPediaFilters["Speciality"].map(
        (spec) => Number(spec.id)
      );
    }
    // If no specialities are selected, use all available specialities from tatvaPediaFilters
    else if (tatvaPediaFilters["Speciality"]?.length > 0) {
      filters.specialityId = tatvaPediaFilters["Speciality"].map(
        (spec) => spec.id
      );
    }

    // Check if there are selected Content Type filters
    if (selectedTatvaPediaFilters["Content Type"]?.length > 0) {
      filters.contentTypeId = selectedTatvaPediaFilters["Content Type"].map(
        (type) => Number(type.id)
      );
    }
    // If no content types are selected, use all available content types from tatvaPediaFilters
    else if (tatvaPediaFilters["Content Type"]?.length > 0) {
      filters.contentTypeId = tatvaPediaFilters["Content Type"].map(
        (type) => type.id
      );
    }

    if (selectedTatvaPediaFilters?.Interestpreference?.length) {
      filters.topicId = selectedTatvaPediaFilters?.Interestpreference?.map(
        (type) => Number(type.id)
      );
    } else if (tatvaPediaFilters?.Interestpreference?.length) {
      filters.topicId = tatvaPediaFilters?.Interestpreference?.map((type) =>
        Number(type.id)
      );
    }

    return filters;
  }, [selectedTatvaPediaFilters, tatvaPediaFilters]);

  // Calculate the appropriate page size to get all necessary data in one request
  const totalPageSize = useMemo(() => {
    // Get the count of content types we'll be displaying
    const { selectedContentTypeIds, allContentTypeIds } = contentTypeData;
    const displayContentTypeCount =
      selectedContentTypeIds.length || allContentTypeIds.length || 4;

    // Calculate required content for each section
    const bannerItems =
      getItemsPerContentType("banner", selectedContentTypeIds.length) *
      displayContentTypeCount;
    const latestItems =
      getItemsPerContentType("latest", selectedContentTypeIds.length) *
      displayContentTypeCount;

    // For Top Picks, consider non-selected content types
    const topPicksCount = selectedContentTypeIds.length
      ? allContentTypeIds.length - selectedContentTypeIds.length
      : allContentTypeIds.length;
    const topPicksItems =
      getItemsPerContentType("topPicks", selectedContentTypeIds.length) *
      Math.max(1, topPicksCount);

    // Add buffer for fallback scenarios (20% extra)
    const totalItems = Math.ceil(
      (bannerItems + latestItems + topPicksItems) * 1.2
    );

    return Math.max(MIN_PAGE_SIZE, totalItems);
  }, [contentTypeData]);

  // Create a string version of filters for comparison to detect changes
  const filterString = useMemo(
    () => JSON.stringify(prepareFilters),
    [prepareFilters]
  );

  // Track previous filter string to detect changes
  const prevFilterStringRef = useRef("");

  // Fetch all content in a single API call
  const {
    data: allContentData,
    loading: contentLoading,
    error: contentError,
  } = useFetchContent({
    filters: prepareFilters,
    pagination: {
      page: 1,
      pageSize: totalPageSize,
    },
    // Only enable when debounced AND either it's our first call or the filters have changed
    enabled:
      debouncedApiCall &&
      (!initialApiCallMade.current ||
        filterString !== prevFilterStringRef.current),
  });

  // Update the ref once API call is made
  useEffect(() => {
    if (debouncedApiCall) {
      initialApiCallMade.current = true;
      prevFilterStringRef.current = filterString;
    }
  }, [debouncedApiCall, filterString]);

  // Process all items from the API response
  const allProcessedItems: ContentItem[] = useMemo(() => {
    return (
      allContentData?.response?.docs?.map(mapApiResponseToContentItem) || []
    );
  }, [allContentData]);

  // Separate content into sections
  const { bannerItems, latestItems, topPicksItems } = useMemo(() => {
    // If API is still loading or no items, return empty arrays
    if (contentLoading || allProcessedItems.length === 0) {
      return {
        bannerItems: [],
        latestItems: [],
        topPicksItems: [],
      };
    }

    // Extract necessary data from contentTypeData
    const { selectedContentTypeIds, allContentTypeIds, contentTypeIdToName } =
      contentTypeData;

    // Determine which content types to use for Banner and Latest sections
    const targetContentTypeIds =
      selectedContentTypeIds.length > 0
        ? selectedContentTypeIds
        : allContentTypeIds;

    // Determine which content types to use for Top Picks section
    // Top Picks should only display non-selected content types to maintain diversity
    // EXCEPT when all content types are selected (treat as default behavior)
    const isAllContentTypesSelected =
      selectedContentTypeIds.length === allContentTypeIds.length;
    const topPicksContentTypeIds = isAllContentTypesSelected
      ? allContentTypeIds // Default behavior: show all content types
      : selectedContentTypeIds.length > 0
      ? allContentTypeIds.filter((id) => !selectedContentTypeIds.includes(id))
      : allContentTypeIds;

    // Get number of items per content type for each section
    const bannerItemsPerType = getItemsPerContentType(
      "banner",
      selectedContentTypeIds.length
    );
    const latestItemsPerType = getItemsPerContentType(
      "latest",
      selectedContentTypeIds.length
    );
    const topPicksItemsPerType = getItemsPerContentType(
      "topPicks",
      selectedContentTypeIds.length
    );

    // Initialize section items
    let bannerItemsResult: ContentItem[] = [];
    let latestItemsResult: ContentItem[] = [];
    let topPicksItemsResult: ContentItem[] = [];

    // Used to track content IDs already displayed to avoid duplicates across sections
    const usedContentIds = new Set<number>();

    // ==========================================
    // BANNER SECTION PROCESSING
    // ==========================================
    //
    // DEFAULT BEHAVIOR:
    // • Displays one latest news from each content type
    //
    // CONTENT TYPE FILTER APPLIED:
    // • 1 Content Type Selected → Show top 3 latest news from the selected content type
    // • 2 Content Types Selected → Show 2 latest news per selected content type
    // • 3+ Content Types Selected → Show only 1 latest news per selected content type
    //
    // FALLBACK HANDLING:
    // • If the selected content type doesn't have enough latest content,
    //   fetch the next most relevant content from the same type or a closely related genre
    // • If no new content exists, replace it with a suggested trending article from another content type
    // ==========================================

    // Fill Banner section (latest news from each content type)
    targetContentTypeIds.forEach((typeId) => {
      const typeName = contentTypeIdToName[typeId];

      // Get all items of this content type and sort by publish date (newest first)
      // Note: In a real API we'd sort by publishedDate, using viewCount as proxy here
      const itemsOfType = allProcessedItems
        .filter((item) => item.contentTypeId === typeId)
        .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0));

      // Take the required number of items for the banner
      const selectedItems = itemsOfType.slice(0, bannerItemsPerType);

      // Add items to banner section
      bannerItemsResult = addItemsToSection(
        selectedItems,
        bannerItemsResult,
        usedContentIds
      );

      // BANNER SECTION FALLBACK HANDLING:
      // If the selected content type doesn't have enough latest content,
      // implement the following 3-step fallback strategy:
      if (selectedItems.length < bannerItemsPerType) {
        const remainingCount = bannerItemsPerType - selectedItems.length;

        // STEP 1: Fetch the next most relevant content from the same content type
        // This maintains content type relevance even if we need to reuse some items
        const moreFromSameType = allProcessedItems
          .filter(
            (item) =>
              item.contentTypeId === typeId &&
              !selectedItems.some((selected) => selected.id === item.id)
          )
          .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
          .slice(0, remainingCount);

        if (moreFromSameType.length > 0) {
          // Add these items but don't mark as used to prevent duplication across sections
          bannerItemsResult = [...bannerItemsResult, ...moreFromSameType];

          // If we've filled the gap, continue to the next content type
          if (moreFromSameType.length === remainingCount) {
            return;
          }
        }

        // STEP 2: Find content from closely related genres (same type or different type)
        const stillNeeded = remainingCount - (moreFromSameType?.length || 0);

        // Get genres from this content type to find related content
        const genres = itemsOfType.flatMap((item) => item.genres || []);

        // Find items with similar genres but different content type
        const relatedItems = findRelatedContentByGenre(
          allProcessedItems,
          genres,
          stillNeeded,
          typeId,
          usedContentIds
        );

        bannerItemsResult = addItemsToSection(
          relatedItems,
          bannerItemsResult,
          usedContentIds
        );

        // STEP 3: Replace with suggested trending articles from other content types
        // This is the last resort when no relevant content exists
        if (relatedItems.length < stillNeeded) {
          const finallyNeeded = stillNeeded - relatedItems.length;
          const trendingItems = findTrendingContent(
            allProcessedItems,
            finallyNeeded,
            usedContentIds
          );

          bannerItemsResult = addItemsToSection(
            trendingItems,
            bannerItemsResult,
            usedContentIds
          );
        }
      }
    });

    // Determine max items for Latest section
    // When all content types are selected (or default case with no selections), limit to 6 items
    const MAX_LATEST_ITEMS = 6;
    const shouldLimitLatestItems =
      selectedContentTypeIds.length === 0 ||
      selectedContentTypeIds.length === allContentTypeIds.length;

    let latestItemsCount = 0;
    const maxLatestItemsTotal = shouldLimitLatestItems
      ? MAX_LATEST_ITEMS
      : Number.MAX_SAFE_INTEGER;

    // ==========================================
    // LATEST SECTION PROCESSING
    // ==========================================
    //
    // DEFAULT BEHAVIOR:
    // • Displays secondary latest content from all content types
    //
    // CONTENT TYPE FILTER APPLIED:
    // • 1 Content Type Selected → Show top 3 secondary latest news from the selected content type
    // • 2 Content Types Selected → Show 2 secondary latest news per selected content type
    // • 3+ Content Types Selected → Show only 1 secondary latest news per selected content type
    //
    // FALLBACK HANDLING:
    // • If the selected content type has insufficient articles,
    //   implement the following 2-step fallback strategy:
    // • Auto-extend the list with the next available articles from the same genre
    // • If no new content exists, replace with suggested trending articles from other content types
    // ==========================================

    // Fill Latest section (secondary content)
    for (const typeId of targetContentTypeIds) {
      // Stop adding items if we've reached the maximum
      if (latestItemsCount >= maxLatestItemsTotal) break;

      const typeName = contentTypeIdToName[typeId];

      // Calculate how many items we can still add for this content type
      const itemsToAdd = shouldLimitLatestItems
        ? Math.min(latestItemsPerType, maxLatestItemsTotal - latestItemsCount)
        : latestItemsPerType;

      if (itemsToAdd <= 0) continue;

      // Get all items of this content type, excluding already used items
      const availableItems = allProcessedItems
        .filter(
          (item) =>
            item.contentTypeId === typeId && !usedContentIds.has(item.id)
        )
        .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0)); // Sort by popularity

      // Take the required number of items for the latest section
      const selectedItems = availableItems.slice(0, itemsToAdd);

      // Update count of items added to Latest section
      latestItemsCount += selectedItems.length;

      // Add items to latest section
      latestItemsResult = addItemsToSection(
        selectedItems,
        latestItemsResult,
        usedContentIds
      );

      // LATEST SECTION FALLBACK HANDLING:
      // If the selected content type has insufficient articles,
      // implement the following 2-step fallback strategy:
      if (selectedItems.length < itemsToAdd) {
        // Calculate how many more items we need
        const remainingCount = Math.min(
          itemsToAdd - selectedItems.length,
          maxLatestItemsTotal - latestItemsCount
        );

        if (remainingCount <= 0) continue;

        // STEP 1: Auto-extend the list with the next available articles from the same genre
        // Get genres from the selected items or from the content type in general
        const genres =
          selectedItems.length > 0
            ? selectedItems.flatMap((item) => item.genres || [])
            : allProcessedItems
                .filter((item) => item.contentTypeId === typeId)
                .flatMap((item) => item.genres || []);

        // Find articles with the same genre - gives us contextually relevant content
        const sameGenreItems = allProcessedItems
          .filter(
            (item) =>
              !usedContentIds.has(item.id) &&
              item.contentTypeId !== typeId && // Different content type
              item.genres?.some((genre) => genres.includes(genre)) // But same genre
          )
          .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
          .slice(0, remainingCount);

        // Update count of items added to Latest section
        latestItemsCount += sameGenreItems.length;

        latestItemsResult = addItemsToSection(
          sameGenreItems,
          latestItemsResult,
          usedContentIds
        );

        // STEP 2: If no new content exists, replace with suggested trending articles from other content types
        if (
          sameGenreItems.length < remainingCount &&
          latestItemsCount < maxLatestItemsTotal
        ) {
          const stillNeeded = Math.min(
            remainingCount - sameGenreItems.length,
            maxLatestItemsTotal - latestItemsCount
          );

          // Get trending articles from any content type as a last resort
          const trendingItems = findTrendingContent(
            allProcessedItems,
            stillNeeded,
            usedContentIds
          );

          // Update count of items added to Latest section
          latestItemsCount += trendingItems.length;

          latestItemsResult = addItemsToSection(
            trendingItems,
            latestItemsResult,
            usedContentIds
          );
        }
      }

      // If we've reached the maximum, stop processing more content types
      if (latestItemsCount >= maxLatestItemsTotal) break;
    }

    // ==========================================
    // TOP PICKS SECTION PROCESSING
    // ==========================================
    //
    // DEFAULT BEHAVIOR:
    // • Displays top 3 tertiary picks from each content type
    // • The hierarchy of content types in this section is reversed compared to the top-level Content Type Filter selection
    //
    // CONTENT TYPE FILTER APPLIED:
    // • The first two sections (Banner + Latest) focus on the selected content types
    // • This section (Top Picks) only displays non-selected content types to maintain content diversity
    // • Ensures users discover content from other areas they haven't specifically filtered for
    //
    // TERTIARY PICKS LOGIC:
    // • Excludes content already used in Banner (primary) and Latest (secondary) sections
    // • Sorts by a combination of engagement metrics and recency for diverse content discovery
    // ==========================================

    // Create a reversed hierarchy of content types for Top Picks
    const reversedTopPicksContentTypeIds = [
      ...topPicksContentTypeIds,
    ].reverse();

    // Fill Top Picks section (focus on non-selected content types for diversity)
    reversedTopPicksContentTypeIds.forEach((typeId) => {
      const typeName = contentTypeIdToName[typeId];

      // Get all items of this content type, excluding already used items from Banner and Latest
      const availableItems = allProcessedItems
        .filter(
          (item) =>
            item.contentTypeId === typeId && !usedContentIds.has(item.id) // Exclude content already used in Banner and Latest
        )
        .sort((a, b) => {
          // Sort for tertiary picks: combination of like count, view count, and recency
          // This provides diverse content discovery beyond just the most popular items
          const aScore =
            (a.like_count || 0) * 0.3 +
            (a.viewCount || 0) * 0.4 +
            (new Date(a.publishedDate || 0).getTime() / 1000000) * 0.3;
          const bScore =
            (b.like_count || 0) * 0.3 +
            (b.viewCount || 0) * 0.4 +
            (new Date(b.publishedDate || 0).getTime() / 1000000) * 0.3;
          return bScore - aScore;
        });

      // Take up to 3 tertiary picks per content type
      const selectedItems = availableItems.slice(0, topPicksItemsPerType);

      // Add items to top picks section
      topPicksItemsResult = [...topPicksItemsResult, ...selectedItems];

      // Mark these items as used to prevent further duplication within Top Picks
      selectedItems.forEach((item) => usedContentIds.add(item.id));
    });

    // If Top Picks is still empty, ensure we always have content by taking tertiary content
    if (topPicksItemsResult.length === 0) {
      // Get content that hasn't been used in Banner and Latest, or use all if none available
      const fallbackItems = allProcessedItems
        .filter((item) => !usedContentIds.has(item.id))
        .sort((a, b) => {
          // Use the same tertiary sorting logic
          const aScore =
            (a.like_count || 0) * 0.3 +
            (a.viewCount || 0) * 0.4 +
            (new Date(a.publishedDate || 0).getTime() / 1000000) * 0.3;
          const bScore =
            (b.like_count || 0) * 0.3 +
            (b.viewCount || 0) * 0.4 +
            (new Date(b.publishedDate || 0).getTime() / 1000000) * 0.3;
          return bScore - aScore;
        })
        .slice(0, 6);

      // If still no content, use trending content as last resort
      topPicksItemsResult =
        fallbackItems.length > 0
          ? fallbackItems
          : findTrendingContent(allProcessedItems, 6, new Set());
    }

    return {
      bannerItems: bannerItemsResult,
      latestItems: latestItemsResult,
      topPicksItems: topPicksItemsResult,
    };
  }, [allProcessedItems, contentTypeData, contentLoading]);

  // Get available content types for Top Picks tabs - these are non-selected content types
  const topPicksAvailableTypes = useMemo(() => {
    const { selectedContentTypeIds, allContentTypes } = contentTypeData;

    // Handle the case where all content types are selected (treat as default behavior)
    const isAllContentTypesSelected =
      selectedContentTypeIds.length === allContentTypes.length;

    // If all content types are selected, return all types (default behavior)
    // If some content types are selected, return non-selected types
    // If no content types are selected, return all content types
    const availableTypes = isAllContentTypesSelected
      ? allContentTypes // Default behavior: show all content types
      : selectedContentTypeIds.length > 0
      ? allContentTypes.filter(
          (type) => !selectedContentTypeIds.includes(type.id)
        )
      : allContentTypes;

    // Group content types by their display names to combine similar types
    const groupedByDisplayName: Record<string, any> = {};

    availableTypes.forEach((type) => {
      const displayName = getContentTypeDisplayName(type.code);

      if (!groupedByDisplayName[displayName]) {
        // Use the first type as the representative, but with the display name
        groupedByDisplayName[displayName] = {
          ...type,
          code: displayName, // Use display name instead of raw code
          originalTypes: [type], // Keep track of original types for this display name
        };
      } else {
        // Add this type to the existing group
        groupedByDisplayName[displayName].originalTypes.push(type);
      }
    });

    // Convert back to array and sort by sequence in reverse order (reversed hierarchy)
    const groupedTypes = Object.values(groupedByDisplayName);

    // Sort in reverse order compared to the main content type filter selection
    // This implements the "reversed hierarchy" requirement
    const result = groupedTypes.sort((a, b) => {
      // First sort by sequence in reverse order
      if (a.sequence !== b.sequence) {
        return b.sequence - a.sequence;
      }
      // If sequences are equal, sort by display name alphabetically (reversed)
      return b.code.localeCompare(a.code);
    });

    return result;
  }, [contentTypeData]);

  // Ensure the topPicksItems are properly organized by content type
  const enhancedTopPicksItems = useMemo(() => {
    // If we have topPicksItems, return them directly as they're already processed correctly
    if (topPicksItems.length > 0) {
      return topPicksItems;
    }

    // Only do the grouping logic if topPicksItems is empty but we have available types
    // This serves as a fallback to ensure content is always shown
    if (topPicksAvailableTypes.length > 0 && allProcessedItems.length > 0) {
      const groupedByType: Record<number, ContentItem[]> = {};

      // Get all actual content type IDs from available types (including originalTypes)
      const allAvailableTypeIds = topPicksAvailableTypes.flatMap((type) =>
        type.originalTypes && type.originalTypes.length > 0
          ? type.originalTypes.map((t: any) => t.id)
          : [type.id]
      );

      // Initialize groups for each available content type
      allAvailableTypeIds.forEach((typeId) => {
        groupedByType[typeId] = [];
      });

      // Take top items from each available content type
      allAvailableTypeIds.forEach((typeId) => {
        const itemsOfType = allProcessedItems
          .filter((item) => item.contentTypeId === typeId)
          .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
          .slice(0, 3);

        if (itemsOfType.length > 0) {
          groupedByType[typeId] = itemsOfType;
        }
      });

      // Flatten back to array
      const result = Object.values(groupedByType).flat();
      return result;
    }

    return [];
  }, [topPicksItems, topPicksAvailableTypes, allProcessedItems]);

  return {
    bannerItems,
    latestItems,
    topPicksItems: enhancedTopPicksItems,
    topPicksAvailableTypes,
    contentLoading,
    contentError,
    debouncedApiCall,
  };
};

export default useContentData;
